import * as React from 'react';
import { MouseEvent } from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  BaseProps,
  LiteralUnion,
  Mode,
  TagHeight,
  TagIntent,
  TagType,
} from '../common/props';
import {
  ColorDef,
  colorsMapTag,
  ColorType,
  getContrastYIQColor,
  pSBC,
} from '../common/colors';
import { Icon } from '../icon/Icon';
import { mcClose, mcMenuKebab } from '@mc/react-ui-icons';
import { useInverseBg } from '../theme/useInverseBg';
import { Popup, PopupPosition } from '../popover/Popup';

export interface ITagProps extends Omit<BaseProps, 'style'> {
  color?: LiteralUnion<ColorType, string>;
  /**
   * Typ informace.
   */
  intent?: TagIntent;
  /**
   * Kurzíva.
   */
  italic?: boolean;
  heigth?: TagHeight;
  strong?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  size?: undefined | 'lg';
  /**
   * Vizuální typ tagu. 'default' = plný, 'outline' = průhledné pozadí s barevným okrajem.
   */
  type?: TagType;
  closable?: boolean;
  onClose?: () => void;
  inverse?: boolean;
  /**
   * Pokud je uvedeno, bude tag jako dropdown - pokud není uveden iconRight, tak se zorbazí ikona kebab.
   */
  menu?: React.JSX.Element | ((closePopup: () => void) => React.JSX.Element);
  /**
   * TAg implicitně nastavuje closeOnInnerClick na true, protože typicky je tam menu a při kliknutí na položku se má dropdown zavřít,
   * pokud se má při dropdown zobrazit něco jiného bez této funkce,
   * je třeba to předat přes popupProps (closeOnInnerClick: false).
   */
  popupProps?: {
    closeOnInnerClick?: boolean;
    arrow?: boolean;
    className?: string;
    position?: PopupPosition | PopupPosition[];
    mode?: Mode;
    focusContentOnOpen?: boolean;
    nested?: boolean;
  };
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export type TagProps = ITagProps;
/**
 * Komponenta zvýrazněného textu.
 */
export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      className,
      color,
      strong,
      italic,
      children,
      iconLeft,
      iconRight,
      size,
      intent,
      popupProps,
      closable,
      menu,
      heigth = 'default',
      inverse,
      onClose,
      type,
      ...other
    },
    ref
  ) => {
    const { closeOnInnerClick, position, mode, ...restPopupProps } =
      popupProps || {};
    const DEFAULT_POSITIONS: PopupPosition[] = [
      'bottom left',
      'bottom right',
      'top left',
      'top right',
    ];
    const parentInverse = useInverseBg(inverse);
    let cls = classNames(Classes.TAG, className, {
      [Classes.TAG_CLOSABLE]: closable,
      [Classes.TAG_HAS_CONTENT]: children != null,
      [color?.startsWith('#')
        ? Classes.TAG_COLOR + color?.substring(1)
        : Classes.TAG_COLOR + color]: color,
      [Classes.TAG_DEFAULT]: !intent || intent === 'default',
      [Classes.TAG_PRIMARY]: intent === 'primary',
      [Classes.TAG_SECONDARY]: intent === 'secondary',
      [Classes.TAG_DANGER]: intent === 'danger',
      [Classes.TAG_SUCCESS]: intent === 'success',
      [Classes.TAG_WARNING]: intent === 'warning',
      [Classes.TAG_INACTIVE]: intent === 'inactive',
      [Classes.TAG_STRONG]: strong,
      [Classes.TAG_ITALIC]: italic,
      [Classes.TAG_SIZE_DEFAULT]: size !== 'lg',
      [Classes.TAG_SIZE_LG]: size === 'lg',
      [Classes.TAG_MAX_HEIGHT]: heigth === 'default' && size !== 'lg',
      [Classes.TAG_INVERSE]: parentInverse,
      [Classes.TAG_HAS_MENU]: menu,
      [Classes.TAG_TYPE_OUTLINE]: type === 'outline',
    });

    const colorStyle = React.useMemo(() => {
      let style = {};
      if (color) {
        let colorVal: ColorDef = colorsMapTag[color] || {
          bg: color,
          text: getContrastYIQColor(color),
          border: pSBC(-0.29, color),
        };

        if (type === 'outline') {
          const outlineColor = colorVal.outline ?? colorVal.text;
          style = {
            backgroundColor: 'transparent',
            color: outlineColor,
            borderColor: outlineColor,
          };
        } else {
          style = {
            backgroundColor: colorVal.bg,
            color: colorVal.text,
            borderColor: colorVal.border,
          };
        }
      }
      return style;
    }, [color, type]);
    let content;
    const calculatedPosition = React.useMemo(() => {
      return position
        ? Array.isArray(position)
          ? [...position, ...DEFAULT_POSITIONS]
          : [position, ...DEFAULT_POSITIONS]
        : DEFAULT_POSITIONS;
    }, [position]);

    let tag = (
      // @ts-ignore
      <div {...other} className={cls} style={colorStyle} ref={ref}>
        <div className={Classes.TAG_WRAPPER}>
          {iconLeft && (
            <span className={Classes.TAG_ICON_LEFT}>{iconLeft}</span>
          )}
          {children}
          {(menu || iconRight) && (
            <span className={Classes.TAG_ICON_RIGHT}>
              {iconRight || <Icon icon={mcMenuKebab} />}
            </span>
          )}
          {closable && (
            <span className={Classes.TAG_CLOSE_ACTION} onClick={onClose}>
              <Icon fixedWidth icon={mcClose} />
            </span>
          )}
        </div>
      </div>
    );
    if (menu) {
      content = (
        <Popup
          position={calculatedPosition}
          {...restPopupProps}
          mode={mode !== undefined ? mode : 'view'}
          trigger={tag}
        >
          {(closePopup: () => void) => {
            return (
              <div
                onClick={
                  closeOnInnerClick === undefined || closeOnInnerClick
                    ? closePopup
                    : undefined
                }
              >
                {typeof menu === 'function' ? menu(closePopup) : menu}
              </div>
            );
          }}
        </Popup>
      );
    } else {
      content = tag;
    }
    return content;
  }
);
